<template>
  <div v-if="fields && fields.length" class="dynamic-form mt-3">
    <div
      v-for="field in fields"
      :key="field.key"
      v-show="isFieldVisible(field)"
      class="form-group mb-3"
    >
      <label :for="'df-' + field.key" v-if="field.type !== 'checkbox'">
        {{ field.label }}
        <span v-if="field.required" class="required">*</span>
      </label>

      <!-- text -->
      <b-form-input
        v-if="field.type === 'text'"
        v-model="formValues[field.key]"
        :id="'df-' + field.key"
        :placeholder="field.placeholder"
        :required="field.required"
      />

      <!-- textarea -->
      <b-form-textarea
        v-else-if="field.type === 'textarea'"
        v-model="formValues[field.key]"
        :id="'df-' + field.key"
        :placeholder="field.placeholder"
        :required="field.required"
        rows="3"
      />

      <!-- select -->
      <b-form-select
        v-else-if="field.type === 'select'"
        v-model="formValues[field.key]"
        :id="'df-' + field.key"
        :required="field.required"
      >
        <b-form-select-option :value="''" disabled>
          {{ field.placeholder || "Оберіть..." }}
        </b-form-select-option>
        <b-form-select-option
          v-for="opt in field.options"
          :key="opt"
          :value="opt"
        >
          {{ opt }}
        </b-form-select-option>
      </b-form-select>

      <!-- phone -->
      <b-form-input
        v-else-if="field.type === 'phone'"
        v-model="formValues[field.key]"
        type="tel"
        :id="'df-' + field.key"
        :placeholder="field.placeholder || '+380...'"
        :required="field.required"
      />

      <!-- email -->
      <b-form-input
        v-else-if="field.type === 'email'"
        v-model="formValues[field.key]"
        type="email"
        :id="'df-' + field.key"
        :placeholder="field.placeholder"
        :required="field.required"
      />

      <!-- checkbox -->
      <b-form-checkbox
        v-else-if="field.type === 'checkbox'"
        v-model="formValues[field.key]"
        :id="'df-' + field.key"
      >
        {{ field.label }}
        <span v-if="field.required" class="required">*</span>
      </b-form-checkbox>

      <!-- date -->
      <b-form-datepicker
        v-else-if="field.type === 'date'"
        v-model="formValues[field.key]"
        :id="'df-' + field.key"
        :placeholder="field.placeholder || 'Оберіть дату'"
        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
        locale="uk"
        class="w-100"
      />

      <!-- datetime -->
      <div v-else-if="field.type === 'datetime'" class="d-flex">
        <b-form-datepicker
          v-model="datetimeDate[field.key]"
          :placeholder="field.placeholder || 'Дата'"
          :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
          locale="uk"
          class="mr-2"
          @input="updateDatetime(field.key)"
        />
        <b-form-timepicker
          v-model="datetimeTime[field.key]"
          placeholder="Час"
          locale="uk"
          :hour12="false"
          @input="updateDatetime(field.key)"
        />
      </div>

      <!-- file -->
      <fileUploader
        v-else-if="field.type === 'file'"
        :maxFilesCount="1"
        @update="onFieldFileUploaded(field.key, $event)"
      />
    </div>
  </div>
</template>

<script>
import fileUploader from "@/components/file-uploader.vue";
export default {
  components: { fileUploader },
  props: {
    service: { type: Object, required: true },
  },
  data() {
    return {
      formValues: {},
      datetimeDate: {},
      datetimeTime: {},
    };
  },
  computed: {
    fields() {
      return this.service.form_fields || [];
    },
    isFormValid() {
      if (!this.fields.length) return true;
      return this.fields
        .filter((f) => f.required && this.isFieldVisible(f))
        .every((f) => {
          var val = this.formValues[f.key];
          if (f.type === "checkbox") return val === true;
          return val !== undefined && val !== null && val !== "";
        });
    },
  },
  watch: {
    isFormValid: {
      immediate: true,
      handler(val) {
        this.$emit("validation-change", val);
      },
    },
    "service.form_fields": {
      immediate: true,
      handler(fields) {
        if (!fields) return;
        var values = {};
        var dtDate = {};
        var dtTime = {};
        fields.forEach((f) => {
          if (f.type === "checkbox") {
            values[f.key] = false;
          } else {
            values[f.key] = "";
          }
          if (f.type === "datetime") {
            dtDate[f.key] = "";
            dtTime[f.key] = "";
          }
        });
        this.formValues = values;
        this.datetimeDate = dtDate;
        this.datetimeTime = dtTime;
      },
    },
  },
  methods: {
    isFieldVisible(field) {
      if (!field.show_when) return true;
      return this.formValues[field.show_when.field] === field.show_when.value;
    },
    updateDatetime(key) {
      var date = this.datetimeDate[key] || "";
      var time = this.datetimeTime[key] || "";
      if (date && time) {
        var parts = date.split("-");
        this.$set(
          this.formValues,
          key,
          parts[2] + "." + parts[1] + "." + parts[0] + " " + time.substring(0, 5)
        );
      } else {
        this.$set(this.formValues, key, "");
      }
    },
    onFieldFileUploaded(key, files) {
      if (files && files.length > 0) {
        this.$set(this.formValues, key, files[0]);
      } else {
        this.$set(this.formValues, key, "");
      }
    },
    buildFormData() {
      if (!this.fields.length) return [];
      return this.fields
        .filter((f) => this.isFieldVisible(f))
        .map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          value: this.formValues[f.key],
        }));
    },
  },
};
</script>

<style lang="scss" scoped>
.dynamic-form {
  .form-group {
    margin-bottom: 1rem;

    label {
      display: block;
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 8px;
      margin-top: 1rem;
      color: #000;
    }

    select,
    .custom-select {
      width: 100%;
      height: 48px;
      font-size: 18px;
      padding-left: 12px;
      padding-right: 12px;
    }
  }
}
</style>
